import { useState, type HTMLAttributes } from 'react';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import Column from 'antd/lib/table/Column';
import { useTranslation } from 'react-i18next';
import { showDefaultErrorToast } from '@app/utils';
import { usePermissions } from '@data/hooks';
import { useRemoveQuestionCategory } from '@data/hooks/matching/questions';
import { CategoryType, QuestionCategoryView } from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import { AntdGrid, AntdTable, AntdTooltip, AntdTypography, Button } from '@ui/components';

const { useBreakpoint } = AntdGrid;
const { Text } = AntdTypography;

type ClassificationTableProps = {
  categories?: QuestionCategoryView[];
  createCategory?: boolean;
  current: number;
  handleTableChange: (
    { pageSize, current }: TablePaginationConfig,
    sorter: SorterResult<QuestionCategoryView> | SorterResult<QuestionCategoryView>[],
  ) => void;
  includeIcdCodes?: boolean;
  isLoading: boolean;
  openCreateCategory: () => void;
  pageSize: number;
  total: number;
  type: CategoryType;
};

export const ClassificationTable = ({
  type,
  total,
  current,
  pageSize,
  isLoading,
  categories,
  createCategory = false,
  includeIcdCodes = false,
  handleTableChange,
  openCreateCategory,
}: ClassificationTableProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER } = usePermissions();

  const { xl } = useBreakpoint();

  const [removingCategoryIds, setRemovingCategoryIds] = useState<Set<string>>(new Set());

  const { mutate: removeCategory } = useRemoveQuestionCategory();

  const handleRemoveCategory = (categoryId: string, questionId: string) => {
    setRemovingCategoryIds((prev) => new Set(prev).add(categoryId));

    removeCategory(
      { categoryId, questionId },
      {
        onError: (err) => {
          showDefaultErrorToast(err);
          setRemovingCategoryIds((prev) => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
          });
        },
        onSuccess: () => {
          setRemovingCategoryIds((prev) => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
          });
        },
      },
    );
  };

  return (
    <AntdTable
      className="mt-4 rounded-sm"
      dataSource={categories}
      loading={isLoading}
      showSorterTooltip={false}
      pagination={{
        current,
        pageSize,
        total,
        placement: ['bottomStart'],
      }}
      sortDirections={['ascend', 'descend', 'ascend']}
      rowKey={(record: QuestionCategoryView) => record.categoryId}
      onRow={(record: QuestionCategoryView) =>
        ({
          'data-testid': `classification-row-${record.categoryId}`,
        } as HTMLAttributes<HTMLElement>)
      }
      summary={() =>
        !IS_REVIEWER &&
        createCategory && (
          <tr>
            <td className="p-3" colSpan={4}>
              <Button
                icon={<PlusOutlined />}
                type="text"
                iconPlacement="start"
                onClick={openCreateCategory}
                className="text-primary-600"
                data-testid={`add-classification-${type}-button`}
              >
                {t(`question_page.classification.add_${type}`)}
              </Button>
            </td>
          </tr>
        )
      }
      onChange={(
        pagination,
        _,
        sorter: SorterResult<QuestionCategoryView> | SorterResult<QuestionCategoryView>[],
      ) => handleTableChange(pagination, sorter)}
      scroll={{ x: xl ? undefined : 'max-content' }}
    >
      <Column
        title={t('question_page.classification.table.name')}
        dataIndex="name"
        render={(name, category: QuestionCategoryView) => (
          <AntdTooltip title={category.context}>
            <Text>{name}</Text>
          </AntdTooltip>
        )}
        width={xl ? '40%' : '25%'}
      />
      {includeIcdCodes && (
        <Column
          title={t('question_page.classification.table.icd_codes')}
          dataIndex="icdCodes"
          width={135}
          render={(icdCodes: string[]) => icdCodes?.join(', ')}
        />
      )}
      {includeIcdCodes && (
        <Column
          title={t('question_page.classification.table.trial_count')}
          dataIndex="trialCount"
          width={100}
          sorter
          render={(_, category: QuestionCategoryView) => category.icdTrialCount}
        />
      )}
      {!IS_REVIEWER && (
        <Column
          dataIndex="actions"
          key="actions"
          width={30}
          align="end"
          className="text-center text-error"
          render={(_value: boolean, classification: QuestionCategoryView) => (
            <Button
              icon={<DeleteOutlined />}
              onClick={() =>
                handleRemoveCategory(classification.categoryId, classification.questionId)
              }
              loading={removingCategoryIds.has(classification.categoryId)}
            />
          )}
        />
      )}
    </AntdTable>
  );
};
